// Utilities for accessing Firestore and Auth

// Load Firebase
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, query, where, doc, documentId, getDoc, getDocs, setDoc, updateDoc, deleteDoc, addDoc, limit, orderBy, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { getAuth, signInWithEmailAndPassword, signOut as firebaseSignOut, onAuthStateChanged as firebaseOnAuthStateChanged } from 'firebase/auth';

const firebaseConfig = {
  apiKey: "AIzaSyACqic76sAf8CNq3btXZ0ZAenVAyQi0XrE",
  authDomain: "hooked-619e0.firebaseapp.com",
  projectId: "hooked-619e0",
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
export const auth = getAuth(app);

// Get the requested session if appropriate
async function getSession(sessionID) {
  const docRef = doc(db, "sessions", sessionID); // Reference to a specific document
  const docSnap = await getDoc(docRef);
  const data = docSnap.data()
  data.id = doc.id;
  return data;
}

// Listen to a session document for real-time updates. Returns the unsubscribe function.
function listenToSession(sessionID, callback) {
  const docRef = doc(db, "sessions", sessionID);
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      data.id = docSnap.id;
      callback(data);
    }
  });
}

// Asks the host device to keep streaming frequent updates while a premium session is being
// watched. Matches iOS/Android's SessionModificationRequest.Action.stream case — capital D in
// sessionID and the empty-object action payload must stay exact or the host won't recognize it.
function sendStreamRequest(sessionID) {
  const id = crypto.randomUUID();
  return setDoc(doc(db, "session_modification_requests", id), {
    id,
    sessionID,
    date: serverTimestamp(),
    action: { stream: {} },
  });
}

// Gets sessions within the given bounds, optionally filtered to those updated after cutoffDate.
// Composite index required: anchorLocationV2.latitude ASC, anchorLocationV2.longitude ASC,
// lastUpdatedDate DESC.
async function getSessionsWithin(minLatitude, maxLatitude, minLongitude, maxLongitude, cutoffDate) {
  const constraints = [
    where("anchorLocationV2.latitude", ">", minLatitude),
    where("anchorLocationV2.latitude", "<", maxLatitude),
    where("anchorLocationV2.longitude", ">", minLongitude),
    where("anchorLocationV2.longitude", "<", maxLongitude),
    orderBy("anchorLocationV2.latitude"),
    orderBy("anchorLocationV2.longitude"),
    orderBy("lastUpdatedDate", "desc"),
    limit(100),
  ];
  if (cutoffDate) {
    constraints.splice(4, 0, where("lastUpdatedDate", ">", cutoffDate));
  }
  const snap = await getDocs(query(collection(db, "sessions"), ...constraints));
  return snap.docs.map((doc) => { const d = doc.data(); d.id = doc.id; return d; });
}

// ── Auth ──────────────────────────────────────────────────────────────────────

async function signInWithEmail(email, password) {
  return signInWithEmailAndPassword(auth, email, password);
}

async function signOut() {
  return firebaseSignOut(auth);
}

function onAuthStateChanged(callback) {
  return firebaseOnAuthStateChanged(auth, callback);
}

// ── Admin Firestore ───────────────────────────────────────────────────────────

async function getSessionLabel(sessionId) {
  const snap = await getDoc(doc(db, "session_labels", sessionId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

async function getAlertedSessions() {
  const q = query(
    collection(db, "alerted_sessions"),
    where("reviewed", "==", false)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

async function getLabelClasses() {
  const q = query(collection(db, "label_classes"), orderBy("createdAt", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

async function createLabelClass(name) {
  const docRef = await addDoc(collection(db, "label_classes"), {
    name,
    createdAt: serverTimestamp(),
  });
  return { id: docRef.id, name };
}

async function saveSessionLabel(sessionId, labels, status, uid, extra = {}, markReviewed = true) {
  await setDoc(doc(db, "session_labels", sessionId), {
    sessionId,
    labels,
    status,
    labeledAt: serverTimestamp(),
    labeledBy: uid,
    ...extra,
  });
  if (!markReviewed) return;
  // Sessions loaded from the random (non-alerted) queue have no alerted_sessions doc —
  // only mark reviewed if one actually exists.
  const alertedRef = doc(db, "alerted_sessions", sessionId);
  const alertedSnap = await getDoc(alertedRef);
  if (alertedSnap.exists()) {
    await updateDoc(alertedRef, { reviewed: true });
  }
}

// Anchor-placement review is decoupled from alerted_sessions.reviewed (a geometry review
// doesn't consume the generic-triage queue). Without this, the same alerted/random
// sessions would resurface in Anchor Placement mode forever, so callers use this to skip
// sessions that already carry an anchor-placement classification in session_labels.
async function getAnchorReviewedSessionIds(sessionIds) {
  const reviewed = new Set();
  const ids = [...new Set(sessionIds)].filter(Boolean);
  for (let i = 0; i < ids.length; i += 30) {
    const chunk = ids.slice(i, i + 30);
    const snap = await getDocs(
      query(collection(db, "session_labels"), where(documentId(), "in", chunk))
    );
    snap.docs.forEach((d) => {
      if (d.data().suggestedClassification !== undefined) reviewed.add(d.id);
    });
  }
  return reviewed;
}

// Firestore has no native random-row query. sessions uses UUID-style doc IDs, so a
// randomized document-ID cursor query gives an approximately-uniform-random session —
// same prefix-range trick as getReportsForContent below, just a single random cursor
// instead of a fixed prefix.
async function getRandomSession() {
  const cursor = crypto.randomUUID();
  let snap = await getDocs(query(collection(db, "sessions"), where(documentId(), ">=", cursor), limit(1)));
  if (snap.empty) {
    // Wrapped past the end of the ID space — take the first doc instead.
    snap = await getDocs(query(collection(db, "sessions"), orderBy(documentId()), limit(1)));
  }
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

async function getAnchorages() {
  const [anchorageSnap, overrideSnap] = await Promise.all([
    getDocs(collection(db, "anchorages")),
    getDocs(collection(db, "anchorage_name_overrides")),
  ]);
  const overrides = new Map();
  for (const d of overrideSnap.docs) {
    const { name, nameSource } = d.data();
    overrides.set(d.id, { name, nameSource: nameSource || "manual" });
  }
  return anchorageSnap.docs.map((d) => {
    const a = { id: d.id, ...d.data() };
    const ov = overrides.get(d.id);
    if (ov) { a.name = ov.name; a.nameSource = ov.nameSource; }
    return a;
  });
}

async function saveAnchorageOverride(geohash6, name) {
  await setDoc(doc(db, "anchorage_name_overrides", geohash6), { name });
}

async function saveAnchorageOsmName(geohash6, name, osmVersion) {
  const data = { name, nameSource: "osm" };
  if (osmVersion != null) data.osmVersion = osmVersion;
  await setDoc(doc(db, "anchorage_name_overrides", geohash6), data);
}

async function saveAnchorageFeedback(geohash6, { alternateName, notes, currentName, nameSource, osmVersion }) {
  const data = {
    geohash: geohash6,
    currentName: currentName || null,
    nameSource: nameSource || null,
    savedAt: serverTimestamp(),
  };
  if (osmVersion != null) data.osmVersion = osmVersion;
  if (alternateName) data.alternateName = alternateName;
  if (notes) data.notes = notes;
  await setDoc(doc(db, "anchorage_feedback", geohash6), data);
}

// ── Content moderation ───────────────────────────────────────────────────────

async function getFlaggedReviews() {
  const q = query(collection(db, "anchorage_reviews"), where("reportCount", ">", 0));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, contentType: "review", ...d.data() }));
}

async function getFlaggedPhotos() {
  const q = query(collection(db, "anchorage_photos"), where("reportCount", ">", 0));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, contentType: "photo", ...d.data() }));
}

// Doc IDs in content_reports are "{contentType}_{contentId}_{reporterUid}" — range-query
// the prefix, same trick the Cloud Function's aggregateContentReports uses.
async function getReportsForContent(contentType, contentId) {
  const prefix = `${contentType}_${contentId}_`;
  const q = query(
    collection(db, "content_reports"),
    where(documentId(), ">=", prefix),
    where(documentId(), "<", prefix.slice(0, -1) + "`")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

function contentCollectionName(contentType) {
  return contentType === "review" ? "anchorage_reviews" : "anchorage_photos";
}

async function hideContent(contentType, contentId) {
  await updateDoc(doc(db, contentCollectionName(contentType), contentId), { hidden: true });
}

async function unhideContent(contentType, contentId) {
  await updateDoc(doc(db, contentCollectionName(contentType), contentId), { hidden: false });
}

async function dismissReports(contentType, contentId) {
  const reports = await getReportsForContent(contentType, contentId);
  await Promise.all(reports.map((r) => deleteDoc(doc(db, "content_reports", r.id))));
  await updateDoc(doc(db, contentCollectionName(contentType), contentId), { reportCount: 0 });
}

export { getSession, listenToSession, sendStreamRequest, getSessionsWithin, signInWithEmail, signOut, onAuthStateChanged, getAlertedSessions, getRandomSession, getLabelClasses, createLabelClass, saveSessionLabel, getSessionLabel, getAnchorReviewedSessionIds, getAnchorages, saveAnchorageOverride, saveAnchorageOsmName, saveAnchorageFeedback, getFlaggedReviews, getFlaggedPhotos, getReportsForContent, hideContent, unhideContent, dismissReports }